import React, { useEffect, useRef } from 'react';
import IonIcon from '../components/IonIcon';
import PhoneMockup from '../components/PhoneMockup';
import './Home.css';

// Uses the exact same Ionicons icon names as the Budge app
const features = [
  {
    icon: 'swap-horizontal',       // Same as app tab: Transactions
    title: 'Expense & Income Tracking',
    description: 'Log every transaction instantly. Categorize spending, add notes, attach receipts, and set up recurring entries. Search, filter, and view everything grouped by date.',
    tags: ['Categories', 'Receipts', 'Recurring', 'Search'],
    color: '#D1FAE5',
    iconColor: '#1A6B4A',
    large: true,
  },
  {
    icon: 'wallet-outline',        // Same as app dashboard: Net Balance card
    title: 'Smart Dashboard',
    description: 'See your financial health at a glance — income, expenses, net balance, savings rate, donut charts, and AI-powered spending insights.',
    color: '#DBEAFE',
    iconColor: '#2563EB',
  },
  {
    icon: 'calculator',            // Same as app tab: EMI
    title: 'EMI Manager',
    description: 'Track all your loans and EMIs in one place. Auto-calculate installments, monitor progress, and get reminders so you never miss a payment.',
    color: '#FEF3C7',
    iconColor: '#D97706',
  },
  {
    icon: 'pie-chart-outline',     // Same as app profile: Budget & Goals
    title: 'Budget Planning',
    description: 'Set monthly budgets per category and watch live burn bars change color as you spend — green to red, keeping you on track.',
    color: '#FCE7F3',
    iconColor: '#DB2777',
  },
  {
    icon: 'trophy',                // Same as app: Goal completed icon
    title: 'Savings Goals',
    description: 'Set targets, track progress with visual bars, and see exactly how many days are left to reach your dream — vacation, car, or emergency fund.',
    color: '#E0E7FF',
    iconColor: '#4F46E5',
  },
  {
    icon: 'bar-chart',             // Same as app dashboard: Analytics & Charts
    title: 'Analytics & Reports',
    description: 'Beautiful donut charts for category breakdown, bar charts for income vs expenses over 6 months, line charts for savings growth, and comprehensive monthly reports you can export as CSV.',
    tags: ['Charts', 'Monthly Reports', 'CSV Export', 'Insights'],
    color: '#CCFBF1',
    iconColor: '#0D9488',
    large: true,
  },
];

const steps = [
  {
    number: '1',
    title: 'Sign Up Instantly',
    description: 'Create an account with email, Google, or Apple. Set your monthly income, preferred currency, and you\'re ready to go.',
  },
  {
    number: '2',
    title: 'Track Everything',
    description: 'Log your transactions, set up budgets, add your EMIs, and create savings goals. Budge organizes it all beautifully.',
  },
  {
    number: '3',
    title: 'Grow Your Wealth',
    description: 'Get smart insights, monitor spending trends, and watch your savings grow with detailed analytics and reports.',
  },
];

function useInView() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = ref.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return ref;
}

function Home() {
  const featuresRef = useInView();
  const stepsRef = useInView();
  const securityRef = useInView();

  return (
    <main>
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-circle hero-circle-1"></div>
          <div className="hero-circle hero-circle-2"></div>
          <div className="hero-circle hero-circle-3"></div>
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <div className="hero-badge">Your Money, Your Rules</div>
            <h1>Take Control of Your <span className="gradient-text">Finances</span></h1>
            <p className="hero-description">
              Track expenses, manage EMIs, set budgets, and reach your savings goals — all in one beautifully designed app.
            </p>
            <div className="hero-buttons">
              <a href="https://apps.apple.com/in/app/budge-budget-money-tracker/id6761966758" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                <IonIcon name="logo-apple" size={20} /> App Store
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.budge.app" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
                <IonIcon name="logo-google-playstore" size={18} /> Google Play
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">Free to Start</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">6+</span>
                <span className="stat-label">Smart Features</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">Secure</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section" id="features" ref={featuresRef}>
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Features</span>
            <h2>Everything You Need to <span className="gradient-text">Manage Money</span></h2>
            <p>From daily expense tracking to long-term savings goals, Budge has you covered.</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`feature-card reveal ${feature.large ? 'feature-card-lg' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="feature-icon" style={{ background: feature.color, color: feature.iconColor }}>
                  <IonIcon name={feature.icon} size={28} color={feature.iconColor} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                {feature.tags && (
                  <div className="feature-tags">
                    {feature.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works" ref={stepsRef}>
        <div className="container">
          <div className="section-header">
            <span className="section-tag">How It Works</span>
            <h2>Get Started in <span className="gradient-text">3 Simple Steps</span></h2>
          </div>
          <div className="steps-grid">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className="step-connector">
                    <svg width="40" height="24" viewBox="0 0 40 24">
                      <path d="M0 12h36M30 6l6 6-6 6" stroke="#1A6B4A" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
                <div className="step-card reveal" style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="security-section" id="security" ref={securityRef}>
        <div className="container">
          <div className="security-content">
            <div className="security-text">
              <span className="section-tag">Security First</span>
              <h2>Your Data is <span className="gradient-text">Protected</span></h2>
              <p className="security-desc">
                We take your financial privacy seriously. Budge uses industry-standard security measures to keep your data safe.
              </p>
              <div className="security-features">
                <div className="security-item reveal">
                  <div className="security-icon">
                    <IonIcon name="finger-print-outline" size={24} />
                  </div>
                  <div>
                    <h4>Biometric Lock</h4>
                    <p>Secure your app with Face ID or fingerprint authentication.</p>
                  </div>
                </div>
                <div className="security-item reveal">
                  <div className="security-icon">
                    <IonIcon name="shield-checkmark" size={24} />
                  </div>
                  <div>
                    <h4>Firebase Security</h4>
                    <p>Built on Google's Firebase with encrypted data storage and secure authentication.</p>
                  </div>
                </div>
                <div className="security-item reveal">
                  <div className="security-icon">
                    <IonIcon name="lock-closed-outline" size={24} />
                  </div>
                  <div>
                    <h4>Your Data, Your Control</h4>
                    <p>Export your data anytime. Delete your account and all associated data whenever you want.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="security-visual">
              <div className="shield-graphic">
                <svg viewBox="0 0 200 240" fill="none">
                  <path d="M100 10L180 50V120C180 170 140 210 100 230C60 210 20 170 20 120V50L100 10Z"
                    fill="url(#shieldGrad)" stroke="#1A6B4A" strokeWidth="2" />
                  <path d="M80 120L95 135L125 105" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                  <defs>
                    <linearGradient id="shieldGrad" x1="20" y1="10" x2="180" y2="230">
                      <stop offset="0%" stopColor="#1A6B4A" />
                      <stop offset="100%" stopColor="#2ECC71" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Start Your Financial Journey Today</h2>
            <p>Join thousands of users who are taking control of their money with Budge. Free to download, no credit card required.</p>
            <div className="hero-buttons" style={{ justifyContent: 'center' }}>
              <a href="https://apps.apple.com/in/app/budge-budget-money-tracker/id6761966758" target="_blank" rel="noopener noreferrer" className="btn btn-white btn-lg">
                <IonIcon name="logo-apple" size={20} /> App Store
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.budge.app" target="_blank" rel="noopener noreferrer" className="btn btn-outline-white btn-lg">
                <IonIcon name="logo-google-playstore" size={18} /> Google Play
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
